import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Navbar } from '../components/Navbar';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator,
} from '../components/ui/dropdown-menu';
import { Textarea } from '../components/ui/textarea';
import {
  RefreshCw, Check, X as XIcon, Repeat, ChevronDown, ChevronUp,
  Copy as CopyIcon, Clock, Music2, Hash, GitCompare, Circle, AlertTriangle,
  Send,
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const PAGE_SIZE = 24;
const TEMPLATES = ['pick_reveal', 'win_recap', 'stats_card', 'preview_match', 'meme_fail', 'countdown'];
const MOODS = ['hype', 'epic', 'chill', 'dramatic', 'meme', 'football_stadium'];
const STATUSES = ['pending_review', 'approved', 'rejected', 'regenerate_requested', 'published'];

const STATUS_STYLES = {
  pending_review:       { bg: 'bg-amber-500/15',  text: 'text-amber-300',  border: 'border-amber-500/30',  label: 'PENDING' },
  approved:             { bg: 'bg-emerald-500/15', text: 'text-emerald-300', border: 'border-emerald-500/30', label: 'APPROVED' },
  rejected:             { bg: 'bg-rose-500/15',    text: 'text-rose-300',    border: 'border-rose-500/30',    label: 'REJECTED' },
  regenerate_requested: { bg: 'bg-sky-500/15',     text: 'text-sky-300',     border: 'border-sky-500/30',     label: 'REGEN' },
  published:            { bg: 'bg-purple-500/15',  text: 'text-purple-300',  border: 'border-purple-500/30',  label: 'PUBLISHED' },
};
const MOOD_CHIP = {
  hype:             { bg: 'bg-pink-600/20',    text: 'text-pink-300' },
  epic:             { bg: 'bg-purple-600/20',  text: 'text-purple-300' },
  chill:            { bg: 'bg-sky-600/20',     text: 'text-sky-300' },
  dramatic:         { bg: 'bg-zinc-700/40',    text: 'text-zinc-200' },
  meme:             { bg: 'bg-yellow-500/20',  text: 'text-yellow-300' },
  football_stadium: { bg: 'bg-emerald-600/20', text: 'text-emerald-300' },
};

function adminHeaders() {
  const k = localStorage.getItem('admin_key') || '';
  return { 'X-Admin-Key': k };
}
function fmtRelative(iso) {
  if (!iso) return '—';
  const dt = new Date(iso);
  const mins = Math.floor((Date.now() - dt.getTime()) / 60000);
  if (mins < 1) return 'ahora';
  if (mins < 60) return `hace ${mins}m`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `hace ${hrs}h`;
  const days = Math.floor(hrs / 24);
  return `hace ${days}d`;
}

function CopyButton({ text, label }) {
  return (
    <button
      className="inline-flex items-center gap-1 text-xs text-zinc-400 hover:text-white"
      data-testid={`copy-${label}`}
      onClick={(e) => {
        e.stopPropagation();
        navigator.clipboard.writeText(text || '');
        toast.success(`${label} copiado`);
      }}
    >
      <CopyIcon className="w-3 h-3" /> copiar
    </button>
  );
}

function StatusCounters({ counts, total }) {
  const order = ['pending_review', 'approved', 'rejected', 'regenerate_requested', 'published'];
  return (
    <div className="flex items-center gap-3 flex-wrap">
      <div className="text-zinc-400 text-sm">Total: <span className="text-white font-semibold">{total}</span></div>
      {order.map((st) => {
        const s = STATUS_STYLES[st];
        return (
          <Badge key={st} variant="outline" className={`${s.bg} ${s.text} ${s.border}`} data-testid={`count-${st}`}>
            {s.label}: {counts?.[st] || 0}
          </Badge>
        );
      })}
    </div>
  );
}

function ReelCard({ item, focused, onFocus, onApprove, onReject, onRegenerate, onOpenDiff, onPublishNow, healthStatus }) {
  const [expanded, setExpanded] = useState(false);
  const status = item.review_status || 'pending_review';
  const st = STATUS_STYLES[status] || STATUS_STYLES.pending_review;
  const moodChip = MOOD_CHIP[item.mood] || MOOD_CHIP.hype;
  const copy = item.copy_data || {};
  const attempts = item.attempts ?? 1;
  const retryOf = item.retry_of;  // new asset (regen) has this pointing back
  const videoRef = useRef(null);

  return (
    <Card
      className={`bg-zinc-900/70 border transition-all ${focused ? 'border-emerald-400 shadow-[0_0_0_2px_rgba(52,211,153,0.35)]' : 'border-white/5 hover:border-white/15'}`}
      data-testid={`reel-card-${item.id}`}
      tabIndex={-1}
      onClick={onFocus}
    >
      <CardContent className="p-3 space-y-3">
        <div className="relative rounded-lg overflow-hidden bg-black aspect-[9/16]">
          <video
            ref={videoRef}
            src={item.file_url}
            controls
            preload="metadata"
            playsInline
            className="w-full h-full object-contain"
            data-testid={`reel-video-${item.id}`}
          />
          <Badge variant="outline" className={`absolute top-2 right-2 ${st.bg} ${st.text} ${st.border}`}>
            {st.label}
          </Badge>
        </div>

        <div className="flex items-center justify-between gap-2">
          <div className="flex flex-wrap items-center gap-1.5 text-xs">
            <Badge variant="outline" className="bg-white/5 text-zinc-200 border-white/10">
              {item.template}
            </Badge>
            <Badge variant="outline" className={`${moodChip.bg} ${moodChip.text} border-white/10`}>
              {item.mood}
            </Badge>
            <Badge variant="outline" className="bg-white/5 text-zinc-300 border-white/10">
              <Clock className="w-3 h-3 mr-1" /> {item.duration_s}s
            </Badge>
            <Badge variant="outline" className="bg-white/5 text-zinc-300 border-white/10" title="intentos">
              <Repeat className="w-3 h-3 mr-1" /> {attempts}
            </Badge>
            {(retryOf || status === 'regenerate_requested') && (
              <button
                onClick={(e) => { e.stopPropagation(); onOpenDiff(item); }}
                className="inline-flex items-center gap-1 bg-sky-900/40 text-sky-300 border border-sky-500/30 rounded-full px-2 py-0.5 text-xs hover:bg-sky-900/60"
                data-testid={`retry-of-${item.id}`}
                title={retryOf ? `Regenerado desde ${retryOf}` : 'Reel marcado para regenerar'}
              >
                <GitCompare className="w-3 h-3" />
                {retryOf ? 'retry of' : 'regen'}
              </button>
            )}
          </div>
        </div>

        <div className="flex items-center gap-2 text-xs text-zinc-400">
          <Music2 className="w-3.5 h-3.5 text-emerald-400" />
          <span className="truncate flex-1" title={item.music_title || ''}>{item.music_title || '—'}</span>
          {item.mood && (
            <span className="text-zinc-500 text-[10px] uppercase whitespace-nowrap">{item.mood}</span>
          )}
        </div>

        <button
          className="w-full flex items-center justify-between text-xs text-zinc-400 hover:text-white"
          onClick={(e) => { e.stopPropagation(); setExpanded(!expanded); }}
          data-testid={`toggle-copy-${item.id}`}
        >
          <span>Ver copy</span>
          {expanded ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
        </button>
        {expanded && (
          <div className="space-y-2 text-xs bg-black/40 rounded-md p-3 border border-white/5">
            <div>
              <div className="flex items-center justify-between text-zinc-500 mb-1">
                <span className="uppercase tracking-wider">Hook</span>
                <CopyButton text={copy.hook} label="hook" />
              </div>
              <div className="text-zinc-100">{copy.hook || '—'}</div>
            </div>
            <div>
              <div className="flex items-center justify-between text-zinc-500 mb-1">
                <span className="uppercase tracking-wider">Caption IG</span>
                <CopyButton text={copy.caption_ig} label="caption IG" />
              </div>
              <div className="text-zinc-100 whitespace-pre-wrap">{copy.caption_ig || '—'}</div>
            </div>
            <div>
              <div className="flex items-center justify-between text-zinc-500 mb-1">
                <span className="uppercase tracking-wider">Caption TikTok</span>
                <CopyButton text={copy.caption_tiktok} label="caption TikTok" />
              </div>
              <div className="text-zinc-100 whitespace-pre-wrap">{copy.caption_tiktok || '—'}</div>
            </div>
            <div>
              <div className="flex items-center justify-between text-zinc-500 mb-1">
                <span className="uppercase tracking-wider flex items-center gap-1"><Hash className="w-3 h-3" /> Hashtags</span>
                <CopyButton text={(copy.hashtags || []).join(' ')} label="hashtags" />
              </div>
              <div className="flex flex-wrap gap-1">
                {(copy.hashtags || []).map((h, i) => (
                  <span key={i} className="text-sky-300 bg-sky-900/30 px-1.5 py-0.5 rounded text-[10px]">{h}</span>
                ))}
              </div>
            </div>
          </div>
        )}

        <div className="flex items-center gap-2 pt-1">
          <Button
            size="sm"
            className="flex-1 bg-emerald-600 hover:bg-emerald-500 text-white"
            onClick={(e) => { e.stopPropagation(); onApprove(item); }}
            data-testid={`approve-${item.id}`}
            disabled={status === 'approved' || status === 'published'}
          >
            <Check className="w-3.5 h-3.5 mr-1" /> Aprobar
          </Button>
          <Button
            size="sm"
            variant="outline"
            className="flex-1 border-sky-500/40 text-sky-300 hover:bg-sky-500/10"
            onClick={(e) => { e.stopPropagation(); onRegenerate(item); }}
            data-testid={`regenerate-${item.id}`}
          >
            <RefreshCw className="w-3.5 h-3.5 mr-1" /> Regenerar
          </Button>
          <Button
            size="sm"
            variant="outline"
            className="flex-1 border-rose-500/40 text-rose-300 hover:bg-rose-500/10"
            onClick={(e) => { e.stopPropagation(); onReject(item); }}
            data-testid={`reject-${item.id}`}
            disabled={status === 'rejected'}
          >
            <XIcon className="w-3.5 h-3.5 mr-1" /> Rechazar
          </Button>
        </div>

        {(status === 'approved' || status === 'published') && onPublishNow && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                size="sm"
                variant="outline"
                className="w-full border-white/10 text-white hover:bg-white/5"
                onClick={(e) => e.stopPropagation()}
                data-testid={`publish-now-${item.id}`}
              >
                <Send className="w-3.5 h-3.5 mr-1" /> Publicar ahora ▾
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="bg-zinc-950 border-white/10 text-white">
              <DropdownMenuLabel className="text-zinc-400 text-xs">Elige plataforma</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                disabled={!healthStatus?.meta?.connected}
                onClick={(e) => { e.stopPropagation(); onPublishNow(item, 'instagram'); }}
                data-testid={`publish-ig-${item.id}`}
                className="cursor-pointer"
              >
                <span className="w-2 h-2 rounded-full bg-[#FF006E] mr-2" />
                Instagram {!healthStatus?.meta?.connected && <span className="text-zinc-500 ml-auto text-[10px]">(sin OAuth)</span>}
              </DropdownMenuItem>
              <DropdownMenuItem
                disabled
                className="cursor-not-allowed opacity-60"
              >
                <span className="w-2 h-2 rounded-full bg-black mr-2 border border-zinc-500" />
                TikTok <span className="text-zinc-500 ml-auto text-[10px]">(Sprint 4b)</span>
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                disabled={!healthStatus?.telegram?.free?.connected}
                onClick={(e) => { e.stopPropagation(); onPublishNow(item, 'telegram_free'); }}
                data-testid={`publish-tgf-${item.id}`}
                className="cursor-pointer"
              >
                <span className="w-2 h-2 rounded-full bg-[#0088CC] mr-2" />
                Telegram Free
              </DropdownMenuItem>
              <DropdownMenuItem
                disabled={!healthStatus?.telegram?.vip?.connected}
                onClick={(e) => { e.stopPropagation(); onPublishNow(item, 'telegram_vip'); }}
                data-testid={`publish-tgv-${item.id}`}
                className="cursor-pointer"
              >
                <span className="w-2 h-2 rounded-full bg-[#FFBE0B] mr-2" />
                Telegram VIP
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}

        <div className="text-[10px] text-zinc-500">
          {fmtRelative(item.created_at)} · pick <span className="font-mono">{(item.pick_id || '').toString().slice(0, 10)}</span>
        </div>
      </CardContent>
    </Card>
  );
}

export default function AdminReels() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(false);
  const [focusIndex, setFocusIndex] = useState(0);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectTarget, setRejectTarget] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [diffOpen, setDiffOpen] = useState(false);
  const [diffPair, setDiffPair] = useState({ current: null, original: null });
  const [metaStatus, setMetaStatus] = useState(null);
  const [publisherHealth, setPublisherHealth] = useState(null);

  const status = searchParams.get('status') || '';
  const template = searchParams.get('template') || '';
  const mood = searchParams.get('mood') || '';
  const sort = searchParams.get('sort') || 'created_desc';
  const offsetParam = parseInt(searchParams.get('offset') || '0', 10);

  const updateParam = useCallback((key, value) => {
    const next = new URLSearchParams(searchParams);
    if (!value || value === 'all') next.delete(key); else next.set(key, value);
    if (key !== 'offset') next.delete('offset');
    setSearchParams(next);
  }, [searchParams, setSearchParams]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const qs = new URLSearchParams();
      qs.set('limit', String(PAGE_SIZE));
      qs.set('offset', String(offsetParam));
      qs.set('since_days', '7');
      if (status) qs.set('status', status);
      if (template) qs.set('template', template);
      if (mood) qs.set('mood', mood);
      const r = await fetch(`${API_URL}/api/content/reels?${qs}`, { headers: adminHeaders() });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const d = await r.json();
      let arr = d.items || [];
      // Client-side sort supporting the chosen mode (extra UX)
      arr = [...arr].sort((a, b) => {
        if (sort === 'duration_asc') return (a.duration_s || 0) - (b.duration_s || 0);
        if (sort === 'duration_desc') return (b.duration_s || 0) - (a.duration_s || 0);
        if (sort === 'template') return (a.template || '').localeCompare(b.template || '');
        return new Date(b.created_at || 0) - new Date(a.created_at || 0);
      });
      setItems(arr);
      setCounts(d.counts_by_status || {});
      setFocusIndex((i) => Math.min(i, Math.max(0, arr.length - 1)));
    } catch (e) {
      toast.error(`Error cargando reels: ${e.message}`);
    } finally {
      setLoading(false);
    }
  }, [offsetParam, status, template, mood, sort]);

  useEffect(() => { load(); }, [load]);

  // Fetch Meta connection status for the header badge
  useEffect(() => {
    (async () => {
      try {
        const r = await fetch(`${API_URL}/api/admin/meta/status`, { headers: adminHeaders() });
        if (r.ok) setMetaStatus(await r.json());
      } catch (_e) { /* ignore */ }
    })();
  }, []);

  // Fetch publisher health (for "Publicar ahora" availability per platform)
  useEffect(() => {
    const fetchHealth = async () => {
      try {
        const r = await fetch(`${API_URL}/api/admin/publisher/health`, { headers: adminHeaders() });
        if (r.ok) setPublisherHealth(await r.json());
      } catch (_e) { /* ignore */ }
    };
    fetchHealth();
    const t = setInterval(fetchHealth, 120000);
    return () => clearInterval(t);
  }, []);

  // Auto-refresh every 60s when there are pending_review items
  useEffect(() => {
    const has_pending = (counts?.pending_review || 0) > 0;
    if (!has_pending) return;
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, [counts, load]);

  const totalInView = items.length;
  const totalAll = useMemo(() => Object.values(counts).reduce((a, b) => a + b, 0), [counts]);

  // ========= actions =========
  async function doApprove(item) {
    try {
      const r = await fetch(`${API_URL}/api/content/reels/${item.id}/approve`, {
        method: 'POST', headers: adminHeaders(),
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      toast.success(`Reel aprobado`);
      load();
    } catch (e) { toast.error(`Error aprobando: ${e.message}`); }
  }
  async function doRegenerate(item) {
    if (!window.confirm('¿Regenerar este reel? El actual quedará archivado y se creará un nuevo job.')) return;
    try {
      const r = await fetch(`${API_URL}/api/content/reels/${item.id}/regenerate`, {
        method: 'POST', headers: adminHeaders(),
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      toast.success('Job de regeneración encolado');
      load();
    } catch (e) { toast.error(`Error regenerando: ${e.message}`); }
  }
  async function openDiff(item) {
    // item is either the NEW regen asset (with retry_of) or the OLD asset (status=regenerate_requested)
    try {
      const otherId = item.retry_of || null;
      let original = null, current = item;
      if (otherId) {
        const r = await fetch(`${API_URL}/api/content/reels/${otherId}`, { headers: adminHeaders() });
        if (r.ok) original = await r.json();
      } else {
        // If this IS the original (regenerate_requested), find the newer asset that points to it
        const r = await fetch(`${API_URL}/api/content/reels?retry_of=${item.id}&limit=1`, { headers: adminHeaders() });
        if (r.ok) {
          const d = await r.json();
          const arr = d.items || [];
          if (arr.length > 0) { original = item; current = arr[0]; }
        }
      }
      setDiffPair({ current, original });
      setDiffOpen(true);
    } catch (e) { toast.error(`Error abriendo diff: ${e.message}`); }
  }

  function askReject(item) {
    setRejectTarget(item);
    setRejectReason('');
    setRejectOpen(true);
  }
  async function confirmReject() {
    if (!rejectTarget) return;
    try {
      const r = await fetch(`${API_URL}/api/content/reels/${rejectTarget.id}/reject`, {
        method: 'POST',
        headers: { ...adminHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason: rejectReason || '' }),
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      toast.success('Reel rechazado');
      setRejectOpen(false);
      load();
    } catch (e) { toast.error(`Error rechazando: ${e.message}`); }
  }

  async function doPublishNow(item, platform) {
    const labels = {
      instagram: 'Instagram', tiktok: 'TikTok',
      telegram_free: 'Telegram Free', telegram_vip: 'Telegram VIP',
    };
    if (!window.confirm(`¿Publicar este reel AHORA en ${labels[platform] || platform}?`)) return;
    const toastId = toast.loading(`Publicando en ${labels[platform]}…`);
    try {
      const r = await fetch(`${API_URL}/api/content/dev/test-publish`, {
        method: 'POST',
        headers: { ...adminHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ asset_id: item.id, platform }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || `HTTP ${r.status}`);
      toast.success(
        d.permalink ? `Publicado ✅ · ${labels[platform]}` : `Enviado a ${labels[platform]}`,
        { id: toastId, description: d.permalink || d.external_id, duration: 7000 },
      );
      load();
    } catch (e) {
      toast.error(`Error publicando: ${e.message}`, { id: toastId });
    }
  }

  // ========= hotkeys =========
  useEffect(() => {
    const onKey = (e) => {
      // Skip when typing in inputs/textareas
      const t = e.target;
      if (t && ['INPUT', 'TEXTAREA', 'SELECT'].includes(t.tagName)) return;
      if (!items.length) return;
      const it = items[focusIndex] || items[0];
      if (e.key === 'a' || e.key === 'A') { e.preventDefault(); doApprove(it); }
      else if (e.key === 'r' || e.key === 'R') { e.preventDefault(); doRegenerate(it); }
      else if (e.key === 'x' || e.key === 'X') { e.preventDefault(); askReject(it); }
      else if (e.key === 'ArrowRight') { e.preventDefault(); setFocusIndex((i) => Math.min(items.length - 1, i + 1)); }
      else if (e.key === 'ArrowLeft') { e.preventDefault(); setFocusIndex((i) => Math.max(0, i - 1)); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, focusIndex]);

  return (
    <div className="min-h-screen bg-black text-white">
      <Navbar />
      <main className="pt-20 max-w-7xl mx-auto px-4 sm:px-6 pb-16" data-testid="admin-reels-page">
        {/* Header */}
        <div className="flex items-start justify-between flex-wrap gap-4 mb-4">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Reels generados</h1>
            <p className="text-zinc-400 text-sm mt-1">
              Revisa, aprueba, regenera o rechaza reels antes de publicar. Atajos: <kbd className="bg-zinc-800 px-1 rounded">A</kbd> aprobar · <kbd className="bg-zinc-800 px-1 rounded">R</kbd> regen · <kbd className="bg-zinc-800 px-1 rounded">X</kbd> rechazar · <kbd className="bg-zinc-800 px-1 rounded">←/→</kbd> navegar
            </p>
          </div>
          <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="refresh-btn">
            <RefreshCw className={`w-4 h-4 mr-1 ${loading ? 'animate-spin' : ''}`} /> Refrescar
          </Button>
        </div>

        {/* Meta connection badge */}
        {metaStatus && (
          <div className="mb-3" data-testid="meta-status-badge">
            {metaStatus.connected && (metaStatus.days_until_expiry ?? 99) >= 7 && (
              <Badge variant="outline" className="bg-emerald-500/15 text-emerald-300 border-emerald-500/30">
                <Circle className="w-2.5 h-2.5 mr-1.5 fill-current" />
                Meta conectado · @{metaStatus.ig_username || '—'} · expira en {metaStatus.days_until_expiry} días
              </Badge>
            )}
            {metaStatus.connected && (metaStatus.days_until_expiry ?? 99) < 7 && (
              <a href={`${API_URL}/api/auth/meta/login`} className="inline-block">
                <Badge variant="outline" className="bg-amber-500/15 text-amber-300 border-amber-500/40 hover:bg-amber-500/25">
                  <AlertTriangle className="w-3 h-3 mr-1.5" />
                  Meta expira en {metaStatus.days_until_expiry} días — Renovar token →
                </Badge>
              </a>
            )}
            {!metaStatus.connected && (
              <a href={`${API_URL}/api/auth/meta/login`} className="inline-block">
                <Badge variant="outline" className="bg-rose-500/15 text-rose-300 border-rose-500/40 hover:bg-rose-500/25">
                  <Circle className="w-2.5 h-2.5 mr-1.5 fill-current" />
                  Meta desconectado — Reconectar →
                </Badge>
              </a>
            )}
          </div>
        )}

        <div className="mb-4">
          <StatusCounters counts={counts} total={totalAll} />
        </div>

        {/* Filters */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mb-5" data-testid="reels-filters">
          <Select value={status || 'all'} onValueChange={(v) => updateParam('status', v)}>
            <SelectTrigger className="bg-zinc-900 border-white/10" data-testid="filter-status">
              <SelectValue placeholder="Status (todos)" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos los status</SelectItem>
              {STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={template || 'all'} onValueChange={(v) => updateParam('template', v)}>
            <SelectTrigger className="bg-zinc-900 border-white/10" data-testid="filter-template">
              <SelectValue placeholder="Template (todos)" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos los templates</SelectItem>
              {TEMPLATES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={mood || 'all'} onValueChange={(v) => updateParam('mood', v)}>
            <SelectTrigger className="bg-zinc-900 border-white/10" data-testid="filter-mood">
              <SelectValue placeholder="Mood (todos)" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos los moods</SelectItem>
              {MOODS.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={sort} onValueChange={(v) => updateParam('sort', v)}>
            <SelectTrigger className="bg-zinc-900 border-white/10" data-testid="filter-sort">
              <SelectValue placeholder="Orden" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="created_desc">Más recientes</SelectItem>
              <SelectItem value="duration_asc">Duración ↑</SelectItem>
              <SelectItem value="duration_desc">Duración ↓</SelectItem>
              <SelectItem value="template">Template A-Z</SelectItem>
            </SelectContent>
          </Select>
          <div className="flex items-center justify-end gap-2">
            <Button
              variant="outline" size="sm"
              disabled={offsetParam === 0}
              onClick={() => updateParam('offset', String(Math.max(0, offsetParam - PAGE_SIZE)))}
              data-testid="prev-page"
            >← Anterior</Button>
            <Button
              variant="outline" size="sm"
              disabled={totalInView < PAGE_SIZE}
              onClick={() => updateParam('offset', String(offsetParam + PAGE_SIZE))}
              data-testid="next-page"
            >Siguiente →</Button>
          </div>
        </div>

        {/* Grid */}
        {loading && items.length === 0 && (
          <div className="text-zinc-400 text-center py-10">Cargando reels…</div>
        )}
        {!loading && items.length === 0 && (
          <div className="text-zinc-500 text-center py-10 border border-white/5 rounded-lg bg-zinc-900/40" data-testid="reels-empty">
            Sin reels para los filtros seleccionados.
          </div>
        )}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="reels-grid">
          {items.map((it, idx) => (
            <ReelCard
              key={it.id}
              item={it}
              focused={idx === focusIndex}
              onFocus={() => setFocusIndex(idx)}
              onApprove={doApprove}
              onReject={askReject}
              onRegenerate={doRegenerate}
              onOpenDiff={openDiff}
              onPublishNow={doPublishNow}
              healthStatus={publisherHealth}
            />
          ))}
        </div>
      </main>

      <Dialog open={diffOpen} onOpenChange={setDiffOpen}>
        <DialogContent className="bg-zinc-950 border-white/10 text-white max-w-6xl" data-testid="diff-dialog">
          <DialogHeader>
            <DialogTitle>Comparar Regeneración</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <div className="text-xs uppercase tracking-wider text-sky-300">Nuevo (regen)</div>
              {diffPair.current && (
                <>
                  <video src={diffPair.current.file_url} controls className="w-full rounded-md bg-black" />
                  <div className="text-xs text-zinc-400">
                    {diffPair.current.template} · {diffPair.current.mood} · {diffPair.current.duration_s}s · attempts {diffPair.current.attempts}
                  </div>
                </>
              )}
            </div>
            <div className="space-y-2">
              <div className="text-xs uppercase tracking-wider text-zinc-400">Original</div>
              {diffPair.original ? (
                <>
                  <video src={diffPair.original.file_url} controls className="w-full rounded-md bg-black" />
                  <div className="text-xs text-zinc-400">
                    {diffPair.original.template} · {diffPair.original.mood} · {diffPair.original.duration_s}s · attempts {diffPair.original.attempts}
                  </div>
                </>
              ) : (
                <div className="text-zinc-500 text-sm">Sin reel original encontrado.</div>
              )}
            </div>
          </div>
          <div className="mt-4 space-y-2">
            <div className="text-xs uppercase tracking-wider text-zinc-400">Diff del copy</div>
            {['title', 'subtitle', 'hook', 'caption_ig', 'caption_tiktok'].map((k) => {
              const a = ((diffPair.current?.copy_data) || {})[k] || '';
              const b = ((diffPair.original?.copy_data) || {})[k] || '';
              const changed = a !== b;
              return (
                <div key={k} className="text-xs font-mono">
                  <div className="text-zinc-500">{k}</div>
                  <div className={`rounded p-2 ${changed ? 'bg-emerald-500/10 border border-emerald-500/30' : 'bg-white/5 border border-white/10'}`}>
                    <div className="text-emerald-300">+ {a || '—'}</div>
                    <div className="text-rose-300">- {b || '—'}</div>
                  </div>
                </div>
              );
            })}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDiffOpen(false)} data-testid="diff-close">Cerrar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={rejectOpen} onOpenChange={setRejectOpen}>
        <DialogContent className="bg-zinc-950 border-white/10 text-white" data-testid="reject-dialog">
          <DialogHeader>
            <DialogTitle>Rechazar reel</DialogTitle>
          </DialogHeader>
          <div className="space-y-2">
            <p className="text-sm text-zinc-400">
              Razón (opcional, se guarda en <span className="font-mono">review_notes</span>):
            </p>
            <Textarea
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="Ej: Música muy repetitiva, frame con falta de ortografía, subtítulo mal recortado…"
              className="bg-zinc-900 border-white/10 text-white"
              rows={3}
              data-testid="reject-reason-input"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectOpen(false)} data-testid="reject-cancel">Cancelar</Button>
            <Button
              className="bg-rose-600 hover:bg-rose-500 text-white"
              onClick={confirmReject}
              data-testid="reject-confirm"
            >
              Rechazar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
